import { useState, useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Calendar, Clock, MapPin, Ticket,
  Share2, Heart, User, ExternalLink, CheckCircle,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { formatPrice } from '@/lib/helpers';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { APP_STORE_ID } from '@/utils/appLinks';
import { trackEvent } from '@/lib/analytics';
import { DownloadAppCard } from '@/components/DownloadAppCard';
import { DownloadAppStickyBar } from '@/components/DownloadAppStickyBar';
import { useEventDetail } from '../hooks/useEventDetail';
import { useWebViewCounter } from '../hooks/useWebViewCounter';
import { EventMapPlaceholder } from '../components/EventMapPlaceholder';
import { SeoHead } from '../components/SeoHead';
import { DEFAULT_SEO_TITLE, DEFAULT_SEO_DESCRIPTION } from '../constants/seoDefaults';
import {
  getEventStatus, formatFullDate, formatShortDate, formatDayMonthYear,
  extractEventId, buildEventSlugId, truncate,
} from '../utils/eventUtils';

// ─── Status badge ─────────────────────────────────────────────────────────

const STATUS_STYLES = {
  live:     'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  today:    'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/20',
  tomorrow: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/20',
  upcoming: 'bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/20',
  ended:    'bg-muted text-muted-foreground border-border',
};

function EventStatusBadge({ rawEvent }) {
  const status = getEventStatus(rawEvent);
  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border',
      STATUS_STYLES[status.variant]
    )}>
      {status.variant === 'live' && (
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
      )}
      {status.label}
    </span>
  );
}

// ─── Organizer section ────────────────────────────────────────────────────
// Tenta ler users/{organizerId}; se as Firestore rules bloquearem
// (§9.2 do migration plan), exibe fallback graciosamente.

function OrganizerSection({ organizerId }) {
  const [name, setName] = useState(null);

  useEffect(() => {
    if (!organizerId) return;
    let cancelled = false;
    getDoc(doc(db, 'users', organizerId))
      .then(snap => {
        if (!cancelled && snap.exists()) setName(snap.data()?.name || null);
      })
      .catch(() => {}); // regras bloqueiam leitura pública — silencioso
    return () => { cancelled = true; };
  }, [organizerId]);

  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
        <User className="w-5 h-5 text-muted-foreground" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Organizado por</p>
        <p className="font-semibold text-sm">{name || 'Organizador oficial'}</p>
      </div>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────

function EventDetailSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="w-full h-[300px] sm:h-[340px] md:h-[420px] lg:h-[480px] bg-muted" />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <div className="h-5 w-24 bg-muted rounded-full" />
            <div className="h-8 w-3/4 bg-muted rounded" />
            <div className="h-4 w-1/3 bg-muted rounded" />
            <Separator />
            <div className="space-y-2">
              {[100, 90, 80, 95, 70].map((w, i) => (
                <div key={i} className={`h-3 bg-muted rounded`} style={{ width: `${w}%` }} />
              ))}
            </div>
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-2xl border bg-card p-5 space-y-3">
                <div className="h-4 w-1/2 bg-muted rounded" />
                <div className="h-3 w-3/4 bg-muted rounded" />
                <div className="h-3 w-2/3 bg-muted rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Not found ────────────────────────────────────────────────────────────

function EventNotFound() {
  return (
    <div className="flex flex-col items-center justify-center py-32 px-4 text-center">
      <Ticket className="w-12 h-12 text-muted-foreground mb-4" />
      <h1 className="text-2xl font-bold mb-2">Evento não encontrado</h1>
      <p className="text-muted-foreground mb-6">
        Este evento pode ter sido removido ou ainda não está disponível.
      </p>
      <Button asChild variant="outline">
        <Link to="/">← Voltar para eventos</Link>
      </Button>
    </div>
  );
}

// ─── Card de informação (sidebar) ─────────────────────────────────────────

function InfoCard({ children, className }) {
  return (
    <div className={cn('rounded-2xl border bg-card shadow-sm p-5', className)}>
      {children}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────

export function EventDetailPage() {
  const { slugId } = useParams();
  const id = extractEventId(slugId);
  const { event, loading, notFound, error } = useEventDetail(id);
  useWebViewCounter(event, id);
  const [shareSuccess, setShareSuccess] = useState(false);

  // React Router mantém a mesma instância de EventDetailPage ao navegar de
  // um evento pra outro (só o :id muda) — sem isso, a página abre já
  // rolada pra onde o usuário estava no evento anterior. behavior:
  // 'instant' porque o app tem scroll-behavior: smooth global (App.css),
  // que faria essa troca de página animar visivelmente.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [id]);

  // GA4: view_item uma vez por evento aberto (só quando o evento carregado
  // é o da URL — ao trocar de evento, o anterior pode ainda estar em memória).
  // joyn_event_id em vez de event_id: o gtag reserva event_id (deduplicação)
  // e não o envia como parâmetro.
  useEffect(() => {
    if (!event || event.id !== id) return;
    trackEvent('view_item', {
      joyn_event_id: event.id,
      event_title: event.title,
      event_category: event.category,
      items: [{ item_id: event.id, item_name: event.title, item_category: event.category }],
    });
  }, [event, id]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: event?.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
      }
    } catch {
      // usuário cancelou ou API não suportada
    }
  };

  // Nenhum desses estados tem título/descrição própria ainda — cai no
  // fallback padrão da área pública (mesmo texto da Home) em vez de deixar
  // o <title>/<meta description> parados no que a página anterior deixou.
  const fallbackSeo = <SeoHead title={DEFAULT_SEO_TITLE} description={DEFAULT_SEO_DESCRIPTION} />;

  // Carregando: título ainda não é o do evento — sem page_view aqui (sai
  // quando o SeoHead do evento carregado montar).
  if (loading) return <><SeoHead title={DEFAULT_SEO_TITLE} description={DEFAULT_SEO_DESCRIPTION} track={false} /><EventDetailSkeleton /></>;
  if (notFound || !event) return <>{fallbackSeo}<EventNotFound /></>;
  if (error) return (
    <>
      {fallbackSeo}
      <div className="flex flex-col items-center py-24 px-4 text-center gap-4">
        <p className="text-destructive">{error}</p>
        <Button asChild variant="outline"><Link to="/">← Voltar</Link></Button>
      </div>
    </>
  );

  // Link antigo (só o id) ou slug desatualizado (evento foi renomeado) —
  // manda pra URL canônica atual. O id (fonte da verdade) já resolveu o
  // evento acima; isso só corrige a barra de endereço/compartilhamento.
  const canonicalSlugId = buildEventSlugId(event.title, id);
  if (slugId !== canonicalSlugId) {
    return <Navigate to={`/evento/${canonicalSlugId}`} replace />;
  }

  const dateLabel = event._startDate ? formatFullDate(event._startDate) : '';
  const shortDate = event._startDate ? formatShortDate(event._startDate) : '';
  const hasTicket = !event.isFree && Boolean(event.ticketUrl);

  // Descrição do evento quando existe; senão, uma frase montada com
  // nome + local + data, pra nunca deixar a meta description vazia.
  const seoDescription = truncate(
    event.description ||
    `${event.title} em ${event.location}, ${event.city}${dateLabel ? ` — ${dateLabel}` : ''}.`
  );

  // Mesmo formato do <title> que functions/renderForBots.js serve a bots.
  const seoTitle = `${event.title}${
    event._startDate ? ` — ${formatDayMonthYear(event._startDate)}` : ''
  } — ${event.city || 'Joinville'} | Joyn`;

  return (
    <>
      <SeoHead title={seoTitle} description={seoDescription} />
      {/* Smart App Banner do Safari (iOS) — só nesta página. Helmet à parte
          do SeoHead: meta com name próprio, não colide com description. */}
      <Helmet>
        <meta name="apple-itunes-app" content={`app-id=${APP_STORE_ID}`} />
      </Helmet>
      <DownloadAppStickyBar />

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      {/* Duas camadas: fundo (a mesma imagem, borrada e escurecida, só pra
          ambientação) + poster (a imagem original, em tamanho natural,
          nunca ampliada além do necessário — flyers de Instagram em 4:5 ou
          9:16 costumam vir em baixa resolução, e esticar até a largura da
          tela pixeliza a arte). */}
      <div className="relative w-full h-[300px] sm:h-[340px] md:h-[420px] lg:h-[480px] overflow-hidden bg-slate-900">
        {event.imageUrl ? (
          <>
            {/* Camada 1 — fundo: só ambientação, não precisa de nitidez.
                scale-110 evita a borda clara que o blur cria ao puxar
                pixels transparentes da margem da imagem. */}
            <img
              src={event.imageUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl brightness-[0.55] saturate-125"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/50" />

            {/* Camada 2 — poster: max-h/max-w + w/h auto (em vez de
                object-contain num container fixo) faz a imagem nunca ser
                ampliada além do seu tamanho natural — só encolhe se for
                maior que o espaço disponível. */}
            <div className="absolute inset-0 flex items-center justify-center p-6 sm:p-8">
              <img
                src={event.imageUrl}
                alt={event.title}
                className="max-h-full max-w-full w-auto h-auto object-contain rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl ring-1 ring-white/10"
              />
            </div>
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900" />
        )}

        {/* Botão voltar */}
        <div className="absolute top-4 left-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background/80 backdrop-blur-sm text-sm font-medium hover:bg-background/95 transition-colors shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Eventos
          </Link>
        </div>

        {/* Badge de destaque */}
        {event.boosted && (
          <div className="absolute top-4 right-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/90 text-white text-xs font-bold backdrop-blur-sm shadow">
              ⚡ Destaque
            </span>
          </div>
        )}
      </div>

      {/* ── Conteúdo ───────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 py-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">

          {/* ── Coluna esquerda ──────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-6">

            {/* Status + Título + Categoria */}
            <div className="space-y-3">
              <EventStatusBadge rawEvent={event._raw} />
              <h1 className="text-3xl font-bold leading-tight tracking-tight">
                {event.title}
              </h1>
              <div className="flex flex-wrap items-center gap-2 text-muted-foreground text-sm">
                <Badge variant="outline" className="text-xs">
                  {event.categoryLabel}
                </Badge>
                <span>·</span>
                <span>{event.city}</span>
              </div>
            </div>

            <Separator />

            {/* Data e horário — visível apenas em mobile (no sidebar em desktop) */}
            <div className="lg:hidden space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="font-medium">{dateLabel}</span>
              </div>
              {event.startTime && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span>
                    {event.startTime}
                    {event.endTime ? ` – ${event.endTime}` : ''}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span>{event.location}</span>
              </div>
            </div>

            {/* Descrição */}
            {event.description && (
              <>
                <div>
                  <h2 className="text-base font-semibold mb-3">Sobre este evento</h2>
                  <p className="text-base leading-relaxed text-muted-foreground whitespace-pre-line">
                    {event.description}
                  </p>
                </div>
                <Separator />
              </>
            )}

            {/* Organizador */}
            <div>
              <h2 className="text-base font-semibold mb-3">Organizador</h2>
              <OrganizerSection organizerId={event.organizerId} />
            </div>
          </div>

          {/* ── Sidebar direita ──────────────────────────────────────── */}
          <div className="space-y-4 lg:sticky lg:top-24 lg:self-start">

            {/* Data e horário */}
            <InfoCard>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Data e horário
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium capitalize">{shortDate}</p>
                    <p className="text-xs text-muted-foreground capitalize">{dateLabel}</p>
                  </div>
                </div>
                {event.startTime && (
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <p className="text-sm font-medium">
                      {event.startTime}
                      {event.endTime ? (
                        <span className="text-muted-foreground font-normal"> até {event.endTime}</span>
                      ) : null}
                    </p>
                  </div>
                )}
              </div>
            </InfoCard>

            {/* Local + mapa */}
            <InfoCard>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Local
              </h3>
              <div className="flex items-start gap-3 mb-4">
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">{event.location}</p>
                  <p className="text-xs text-muted-foreground">{event.city}</p>
                </div>
              </div>
              <EventMapPlaceholder
                location={event.location}
                city={event.city}
                latitude={event.latitude}
                longitude={event.longitude}
              />
            </InfoCard>

            {/* Ingressos */}
            <InfoCard>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Ingressos
              </h3>
              <p className={cn(
                'text-2xl font-bold mb-4',
                event.isFree ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'
              )}>
                {event.isFree ? 'Gratuito' : formatPrice(event.ticketPrice)}
              </p>

              {hasTicket && (
                <a
                  href={event.ticketUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground rounded-full px-4 py-3.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                  Comprar ingresso
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}

              {!event.isFree && !event.ticketUrl && (
                <p className="text-xs text-muted-foreground">
                  Informações sobre ingressos disponíveis em breve.
                </p>
              )}
            </InfoCard>

            {/* Ações */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 gap-2 rounded-xl"
                onClick={handleShare}
              >
                {shareSuccess
                  ? <><CheckCircle className="w-4 h-4 text-emerald-500" /> Copiado!</>
                  : <><Share2 className="w-4 h-4" /> Compartilhar</>
                }
              </Button>
              {/* Favoritar — visual; funcional na Fase 3 (auth) */}
              <Button
                variant="outline"
                className="flex-1 gap-2 rounded-xl"
                disabled
                title="Faça login para favoritar"
              >
                <Heart className="w-4 h-4" />
                Favoritar
              </Button>
            </div>

            {/* Convite pro app — quem chega por link compartilhado do app */}
            <DownloadAppCard />

          </div>
        </div>
      </div>
    </>
  );
}
